import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'bookkeepingbillings';

/**
 * Fetch all billed entry IDs
 */
const fetchBilledEntryIds = async (): Promise<string[]> => {
  return apiRequest<void, string[]>(HttpMethod.GET, API_ENDPOINT);
};

/**
 * Mark entries as billed
 */
const markEntriesBilled = async (entryIds: string[]): Promise<void> => {
  return apiRequest<string[], void>(HttpMethod.POST, `${API_ENDPOINT}/billed`, entryIds);
};

/**
 * Mark entries as not billed
 */
const markEntriesNotBilled = async (entryIds: string[]): Promise<void> => {
  return apiRequest<string[], void>(HttpMethod.POST, `${API_ENDPOINT}/notbilled`, entryIds);
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch billed entry IDs
 * Returns a Set for O(1) lookup performance
 */
export const useBilledEntryIdsQuery = () => {
  return useQuery({
    queryKey: queryKeys.billing.billedIds(),
    queryFn: fetchBilledEntryIds,
    select: (data) => new Set(data),
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Mark entries as billed
 */
export const useMarkEntriesBilledMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markEntriesBilled,
    onMutate: async (entryIds) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.billing.billedIds() });

      // Snapshot the previous value
      const previousIds = queryClient.getQueryData<string[]>(queryKeys.billing.billedIds());

      // Optimistically update to the new value
      if (previousIds) {
        queryClient.setQueryData<string[]>(
          queryKeys.billing.billedIds(),
          [...new Set([...previousIds, ...entryIds])]
        );
      }

      return { previousIds };
    },
    onError: (error, _entryIds, context) => {
      // Rollback on error
      if (context?.previousIds) {
        queryClient.setQueryData(queryKeys.billing.billedIds(), context.previousIds);
      }
      showApiError(error, 'save_failed');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.billedIds() });
    },
    onSuccess: () => {
      toastService.success({ title: 'billing.entries_marked_billed' });
    },
  });
};

/**
 * Mark entries as not billed
 */
export const useMarkEntriesNotBilledMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markEntriesNotBilled,
    onMutate: async (entryIds) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.billing.billedIds() });

      // Snapshot the previous value
      const previousIds = queryClient.getQueryData<string[]>(queryKeys.billing.billedIds());

      // Optimistically update - remove the entry IDs
      if (previousIds) {
        const entryIdSet = new Set(entryIds);
        queryClient.setQueryData<string[]>(
          queryKeys.billing.billedIds(),
          previousIds.filter((id) => !entryIdSet.has(id))
        );
      }

      return { previousIds };
    },
    onError: (error, _entryIds, context) => {
      // Rollback on error
      if (context?.previousIds) {
        queryClient.setQueryData(queryKeys.billing.billedIds(), context.previousIds);
      }
      showApiError(error, 'save_failed');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.billedIds() });
    },
    onSuccess: () => {
      toastService.success({ title: 'billing.entries_marked_not_billed' });
    },
  });
};
